import React, { useRef, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useGlobalPresence } from "../components/GlobalPresenceProvider";
import { getAuthToken } from "../utils/auth";
import "../App.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

function WhiteboardPage() {
  const { classId } = useParams();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignment');
  const navigate = useNavigate();

  // Global presence integration
  const { connectToClass, sendActivity } = useGlobalPresence();

  // Connect to class on mount
  useEffect(() => {
    if (classId) {
      connectToClass(parseInt(classId));
    }
  }, [classId, connectToClass]);

  const gridCanvasRef = useRef(null);
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [tool, setTool] = useState("pen");
  const [taskImage, setTaskImage] = useState(null);
  const [taskImagePreview, setTaskImagePreview] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackType, setFeedbackType] = useState("neutral");

  // Assignment mode state
  const [, setAssignmentData] = useState(null);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [strokeHistory, setStrokeHistory] = useState([]);
  const [isChecking, setIsChecking] = useState(false);
  const [tempShape, setTempShape] = useState(null);
  const [baseSnapshot, setBaseSnapshot] = useState(null);
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hideUI, setHideUI] = useState(false);

  // Popup state for feedback
  const [showPopup, setShowPopup] = useState(false);

  // Task image on whiteboard - position and size
  const [taskImagePos, setTaskImagePos] = useState({ x: 40, y: 40 });
  const [taskImageSize, setTaskImageSize] = useState({ width: 300, height: 200 });
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error("Fullscreen error:", err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.error("Exit fullscreen error:", err);
      });
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Load assignment data if assignmentId is present
  useEffect(() => {
    if (assignmentId && classId) {
      loadAssignment();
    }
  }, [assignmentId, classId]);

  const loadAssignment = async () => {
    setAssignmentLoading(true);
    try {
      const token = getAuthToken();
      const response = await fetch(
        `${API_URL}/classes/${classId}/assignments/${assignmentId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load assignment');
      }

      const data = await response.json();
      console.log('Assignment data:', data);
      setAssignmentData(data);

      // Set task image for whiteboard display
      if (data.taskImage) {
        const imgSrc = `data:image/jpeg;base64,${data.taskImage}`;
        setTaskImagePreview(imgSrc);
        setTaskImage({ name: data.title, isAssignment: true, base64: data.taskImage });

        // Load image to get natural dimensions for proper sizing
        const img = new Image();
        img.onload = () => {
          const maxWidth = 400;
          const maxHeight = 300;
          let width = img.naturalWidth;
          let height = img.naturalHeight;

          // Scale down if too large
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          setTaskImageSize({ width, height });
          // Center the image on the canvas
          const centerX = (window.innerWidth - width) / 2;
          const centerY = (window.innerHeight - height) / 2;
          setTaskImagePos({ x: Math.max(0, centerX), y: Math.max(60, centerY) });
        };
        img.src = imgSrc;
      }
    } catch (err) {
      console.error('Failed to load assignment:', err);
      setFeedback("Ülesande laadimine ebaõnnestus");
      setFeedbackType("error");
      setShowPopup(true);
    } finally {
      setAssignmentLoading(false);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'h' || e.key === 'H') {
        setHideUI(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    const initCanvas = () => {
      const gridCanvas = gridCanvasRef.current;
      const drawCanvas = canvasRef.current;
      if (!gridCanvas || !drawCanvas) return;

      const container = gridCanvas.parentElement;
      const rect = container.getBoundingClientRect();

      [gridCanvas, drawCanvas].forEach(canvas => {
        canvas.style.width = rect.width + "px";
        canvas.style.height = rect.height + "px";
        canvas.width = rect.width;
        canvas.height = rect.height;
      });

      const gridCtx = gridCanvas.getContext("2d");
      if (gridCtx) {
        drawGrid(gridCtx, rect.width, rect.height);
      }

      const ctx = drawCanvas.getContext("2d");
      if (!ctx) return;

      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.imageSmoothingEnabled = true;

      ctxRef.current = ctx;
    };

    initCanvas();

    const handleResize = () => {
      const drawCanvas = canvasRef.current;
      if (!drawCanvas) return;

      const imageData = drawCanvas.toDataURL("image/png");
      initCanvas();

      const img = new Image();
      img.onload = () => {
        const container = drawCanvas.parentElement;
        const rect = container.getBoundingClientRect();
        const ctx = ctxRef.current;
        if (ctx) {
          ctx.clearRect(0, 0, rect.width, rect.height);
          ctx.drawImage(img, 0, 0);
        }
      };
      img.src = imageData;
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const drawGrid = useCallback((ctx, width, height) => {
    ctx.clearRect(0, 0, width, height);

    ctx.strokeStyle = "#e5e7eb";
    ctx.lineWidth = 1;
    const gridSize = 40;

    for (let x = 0; x < width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    for (let y = 0; y < height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  }, []);

  const getCoords = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }, []);

  const startDrawing = useCallback(
    (coords) => {
      const ctx = ctxRef.current;
      const canvas = canvasRef.current;
      if (!ctx || !canvas) return;

      const isShapeTool = [
        "line",
        "rectangle",
        "circle",
        "triangle",
        "rhombus",
        "trapezoid",
        "pythagorean",
        "square",
        "plus"
      ].includes(tool);

      if (isShapeTool) {
        const snapshot = canvas.toDataURL("image/png");
        setBaseSnapshot(snapshot);
        setTempShape({ startX: coords.x, startY: coords.y });
        setIsDrawingShape(true);
        return;
      }

      ctx.beginPath();
      ctx.moveTo(coords.x, coords.y);
      setDrawing(true);
    },
    [tool]
  );

  const draw = useCallback(
    (coords) => {
      if (tempShape && isDrawingShape && baseSnapshot) {
        const ctx = ctxRef.current;
        const canvas = canvasRef.current;
        if (!ctx || !canvas) return;

        const img = new Image();
        img.onload = () => {
          const container = canvas.parentElement;
          const rect = container.getBoundingClientRect();
          if (rect) {
            ctx.clearRect(0, 0, rect.width, rect.height);
            ctx.drawImage(img, 0, 0);
            drawShapePreview(ctx, coords);
          }
        };
        img.src = baseSnapshot;
        return;
      }

      if (!drawing) return;

      const ctx = ctxRef.current;
      if (!ctx) return;

      if (tool === "eraser") {
        ctx.globalCompositeOperation = "destination-out";
        ctx.strokeStyle = "rgba(0,0,0,1)";
        ctx.lineWidth = 18;
      } else {
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = "#111827";
        ctx.lineWidth = 3;
      }

      ctx.lineTo(coords.x, coords.y);
      ctx.stroke();

      ctx.globalCompositeOperation = "source-over";
    },
    [tempShape, isDrawingShape, baseSnapshot, drawing, tool]
  );

  const drawShapePreview = useCallback((ctx, coords) => {
    if (!tempShape) return;

    ctx.strokeStyle = "#111827";
    ctx.lineWidth = 3;
    ctx.fillStyle = "transparent";

    const dx = coords.x - tempShape.startX;
    const dy = coords.y - tempShape.startY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    switch (tool) {
      case "line":
        ctx.beginPath();
        ctx.moveTo(tempShape.startX, tempShape.startY);
        ctx.lineTo(coords.x, coords.y);
        ctx.stroke();
        break;

      case "rectangle":
        ctx.strokeRect(tempShape.startX, tempShape.startY, dx, dy);
        break;

      case "circle":
        ctx.beginPath();
        ctx.arc(tempShape.startX, tempShape.startY, dist, 0, 2 * Math.PI);
        ctx.stroke();
        break;

      case "triangle":
        ctx.beginPath();
        ctx.moveTo(tempShape.startX, tempShape.startY + dy);
        ctx.lineTo(tempShape.startX + dx, tempShape.startY + dy);
        ctx.lineTo(tempShape.startX + dx / 2, tempShape.startY);
        ctx.closePath();
        ctx.stroke();
        break;

      case "rhombus":
        ctx.beginPath();
        ctx.moveTo(tempShape.startX + dx / 2, tempShape.startY);
        ctx.lineTo(tempShape.startX + dx, tempShape.startY + dy / 2);
        ctx.lineTo(tempShape.startX + dx / 2, tempShape.startY + dy);
        ctx.lineTo(tempShape.startX, tempShape.startY + dy / 2);
        ctx.closePath();
        ctx.stroke();
        break;

      case "trapezoid":
        ctx.beginPath();
        ctx.moveTo(tempShape.startX + dx / 4, tempShape.startY);
        ctx.lineTo(tempShape.startX + (3 * dx) / 4, tempShape.startY);
        ctx.lineTo(tempShape.startX + dx, tempShape.startY + dy);
        ctx.lineTo(tempShape.startX, tempShape.startY + dy);
        ctx.closePath();
        ctx.stroke();
        break;

      case "square": {
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        const x0 = dx >= 0 ? tempShape.startX : tempShape.startX - size;
        const y0 = dy >= 0 ? tempShape.startY : tempShape.startY - size;
        ctx.strokeRect(x0, y0, size, size);
        break;
      }

      case "pythagorean": {
        const x0 = tempShape.startX;
        const y0 = tempShape.startY;
        const x1 = coords.x;
        const y1 = coords.y;

        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y0);
        ctx.lineTo(x1, y1);
        ctx.lineTo(x0, y0);
        ctx.stroke();
        break;
      }

      case "plus": {
        const size = Math.max(Math.abs(dx), Math.abs(dy));
        
        const centerX = tempShape.startX;
        const centerY = tempShape.startY;
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY - size);
        ctx.lineTo(centerX, centerY + size);
        ctx.stroke();
        
        ctx.beginPath();
        ctx.moveTo(centerX - size, centerY);
        ctx.lineTo(centerX + size, centerY);
        ctx.stroke();
        break;
      }

      default:
        break;
    }
  }, [tempShape, tool]);

  const endDrawing = useCallback(
    (coords) => {
      if (tempShape && isDrawingShape) {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!ctx || !canvas) return;

        drawShapePreview(ctx, coords);

        const snapshot = canvas.toDataURL("image/png");
        setStrokeHistory((prev) => [...prev, snapshot].slice(-50));

        setTempShape(null);
        setBaseSnapshot(null);
        setIsDrawingShape(false);
        return;
      }

      if (!drawing) return;
      setDrawing(false);

      const ctx = ctxRef.current;
      if (ctx) {
        ctx.globalCompositeOperation = "source-over";
      }

      const canvas = canvasRef.current;
      const snapshot = canvas.toDataURL("image/png");
      setStrokeHistory((prev) => [...prev, snapshot].slice(-50));
    },
    [tempShape, isDrawingShape, drawing, drawShapePreview]
  );

  const handlePointerDown = useCallback(
    (e) => {
      e.preventDefault();
      const coords = getCoords(e);
      startDrawing(coords);
      
      // Send activity event to presence system
      if (sendActivity) {
        sendActivity();
      }
    },
    [startDrawing, getCoords, sendActivity]
  );

  const handlePointerMove = useCallback(
    (e) => {
      e.preventDefault();
      draw(getCoords(e));
    },
    [draw, getCoords]
  );

  const handlePointerUp = useCallback(
    (e) => {
      e.preventDefault();
      endDrawing(getCoords(e));
    },
    [endDrawing, getCoords]
  );

  const handlePointerLeave = useCallback(() => {
    setDrawing(false);
    const ctx = ctxRef.current;
    if (ctx) {
      ctx.globalCompositeOperation = "source-over";
    }
  }, []);

  // Touch event handlers to prevent browser gestures that might exit fullscreen
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const container = canvas?.parentElement;
    const rect = container?.getBoundingClientRect();

    if (!ctx || !rect) return;

    ctx.clearRect(0, 0, rect.width, rect.height);

    setStrokeHistory([]);
    setBaseSnapshot(null);
  }, []);

  const undo = useCallback(() => {
    if (!strokeHistory.length) return;

    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    const container = canvas?.parentElement;
    const rect = container?.getBoundingClientRect();

    if (!ctx || !rect) return;

    if (strokeHistory.length === 1) {
      ctx.clearRect(0, 0, rect.width, rect.height);
      setStrokeHistory([]);
    } else {
      ctx.clearRect(0, 0, rect.width, rect.height);

      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = strokeHistory[strokeHistory.length - 2];
      setStrokeHistory((prev) => prev.slice(0, -1));
    }
  }, [strokeHistory]);

  const checkAnswer = useCallback(async () => {
    if (!taskImage) {
      setFeedback("Palun laadi üles ülesande pilt");
      setFeedbackType("error");
      setShowPopup(true);
      return;
    }

    setShowPopup(true);
    setIsChecking(true);
    setFeedback("Tehisintellekt kontrollib...");
    setFeedbackType("neutral");

    try {
      const drawCanvas = canvasRef.current;
      const gridCanvas = gridCanvasRef.current;

      if (!drawCanvas || !gridCanvas) {
        throw new Error("Canvas not found");
      }

      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = drawCanvas.width;
      tempCanvas.height = drawCanvas.height;
      const tempCtx = tempCanvas.getContext('2d');

      if (!tempCtx) throw new Error("Failed to create temp canvas");

      tempCtx.fillStyle = '#ffffff';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      tempCtx.drawImage(gridCanvas, 0, 0);

      // Draw task image onto canvas at its current position
      if (taskImagePreview) {
        const taskImg = new Image();
        await new Promise((resolve, reject) => {
          taskImg.onload = resolve;
          taskImg.onerror = reject;
          taskImg.src = taskImagePreview;
        });
        tempCtx.drawImage(taskImg, taskImagePos.x, taskImagePos.y, taskImageSize.width, taskImageSize.height);
      }

      tempCtx.drawImage(drawCanvas, 0, 0);

      const token = getAuthToken();

      // Send combined whiteboard image (task + solution) as both files
      const canvasBlob = await new Promise((resolve) => {
        tempCanvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.95);
      });

      if (!canvasBlob) throw new Error("Failed to capture canvas");

      const fd = new FormData();
      fd.append("task_file", canvasBlob, "whiteboard.jpg");
      fd.append("user_file", canvasBlob, "whiteboard.jpg");
      if (classId) {
        fd.append("class_id", classId);
      }

      const res = await fetch(`${API_URL}/analyze-image/`, {
        method: "POST",
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: fd
      });

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();

      if (!data.ok) {
        setFeedback(data.feedback || "Serveri viga");
        setFeedbackType("error");
        return;
      }

      if (data.is_correct) {
        setFeedback(data.feedback);
        setFeedbackType("ok");
      } else {
        setFeedback(data.feedback);
        setFeedbackType("neutral");
      }
    } catch (err) {
      console.error("Error:", err);
      const errorMsg = err instanceof Error ? err.message : "Unknown error";
      setFeedback(
        `Viga: ${errorMsg.includes("Failed to fetch") ? "Ühenduse viga" : errorMsg}`
      );
      setFeedbackType("error");
    } finally {
      setIsChecking(false);
    }
  }, [taskImage, taskImagePreview, taskImagePos, taskImageSize, classId]);

  const handleBack = () => {
    navigate('/student/classes');
  };

  return (
    <div className="app">
      <div className="main-content">
        <div className="canvas-container">
          <canvas
            ref={gridCanvasRef}
            className="grid-canvas"
            aria-hidden="true"
          />
          <canvas
            ref={canvasRef}
            id="whiteboard"
            className="drawing-canvas"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            aria-label="Math whiteboard"
          />

          {/* Task image on whiteboard - draggable, no border */}
          {taskImagePreview && (
            <div
              style={{
                position: 'absolute',
                left: taskImagePos.x,
                top: taskImagePos.y,
                width: taskImageSize.width,
                height: taskImageSize.height,
                cursor: isDraggingImage ? 'grabbing' : 'grab',
                userSelect: 'none',
                zIndex: 25
              }}
              onMouseDown={(e) => {
                setIsDraggingImage(true);
                setDragStart({ x: e.clientX - taskImagePos.x, y: e.clientY - taskImagePos.y });
              }}
              onMouseMove={(e) => {
                if (isDraggingImage) {
                  setTaskImagePos({
                    x: Math.max(0, e.clientX - dragStart.x),
                    y: Math.max(0, e.clientY - dragStart.y)
                  });
                }
              }}
              onMouseUp={() => setIsDraggingImage(false)}
              onMouseLeave={() => setIsDraggingImage(false)}
              onTouchStart={(e) => {
                const touch = e.touches[0];
                setIsDraggingImage(true);
                setDragStart({ x: touch.clientX - taskImagePos.x, y: touch.clientY - taskImagePos.y });
              }}
              onTouchMove={(e) => {
                if (!isDraggingImage) return;
                const touch = e.touches[0];
                setTaskImagePos({
                  x: Math.max(0, touch.clientX - dragStart.x),
                  y: Math.max(0, touch.clientY - dragStart.y)
                });
              }}
              onTouchEnd={() => setIsDraggingImage(false)}
            >
              <img
                src={taskImagePreview}
                alt="Ülesanne"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none'
                }}
              />
            </div>
          )}

          {/* Loading indicator when loading assignment */}
          {assignmentLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(255,255,255,0.9)',
              padding: '20px 40px',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100
            }}>
              Laadin ülesannet...
            </div>
          )}

          {classId && (
            <button
              onClick={handleBack}
              className={`action-btn secondary ${hideUI ? 'hidden' : ''}`}
              style={{
                position: 'absolute',
                top: '12px',
                left: '80px',
                zIndex: 60
              }}
            >
              ← Tagasi
            </button>
          )}

          <div className={`left-toolbar ${hideUI ? 'hidden' : ''}`} role="toolbar" aria-label="Drawing tools">
            <button
              className={`tool-circle ${tool === "pen" ? "active" : ""}`}
              onClick={() => setTool("pen")}
              title="Pliiats"
              aria-label="Pen tool"
            >
              ✏️
            </button>
            <button
              className={`tool-circle ${tool === "eraser" ? "active" : ""}`}
              onClick={() => setTool("eraser")}
              title="Kustutuskumm"
              aria-label="Eraser"
            >
              🧽
            </button>
            <button
              className={`tool-circle ${tool === "plus" ? "active" : ""}`}
              onClick={() => setTool("plus")}
              title="Pluss märk"
              aria-label="Plus sign"
            >
              ✚
            </button>
            <div className="toolbar-separator" />
            <button
              className={`tool-circle ${tool === "line" ? "active" : ""}`}
              onClick={() => setTool("line")}
              title="Sirge joon"
              aria-label="Line"
            >
              📏
            </button>
            <button
              className={`tool-circle ${tool === "rectangle" ? "active" : ""}`}
              onClick={() => setTool("rectangle")}
              title="Ristkülik"
              aria-label="Rectangle"
            >
              ▭
            </button>
            <button
              className={`tool-circle ${tool === "circle" ? "active" : ""}`}
              onClick={() => setTool("circle")}
              title="Ring"
              aria-label="Circle"
            >
              ○
            </button>
            <button
              className={`tool-circle ${tool === "triangle" ? "active" : ""}`}
              onClick={() => setTool("triangle")}
              title="Kolmnurk"
              aria-label="Triangle"
            >
              △
            </button>
            <button
              className={`tool-circle ${tool === "rhombus" ? "active" : ""}`}
              onClick={() => setTool("rhombus")}
              title="Rombi"
              aria-label="Rhombus"
            >
              ◇
            </button>
            <button
              className={`tool-circle ${tool === "trapezoid" ? "active" : ""}`}
              onClick={() => setTool("trapezoid")}
              title="Trapets"
              aria-label="Trapezoid"
            >
              ⏥
            </button>
            <button
              className={`tool-circle ${tool === "square" ? "active" : ""}`}
              onClick={() => setTool("square")}
              title="Ruut"
              aria-label="Square"
            >
              ⬜
            </button>
            <button
              className={`tool-circle ${tool === "pythagorean" ? "active" : ""}`}
              onClick={() => setTool("pythagorean")}
              title="Pythagorase kolmnurk"
              aria-label="Pythagorean triangle"
            >
              ◢
            </button>
            <div className="toolbar-separator" />
            <button
              className="tool-circle"
              onClick={clearCanvas}
              title="Puhasta"
              aria-label="Clear canvas"
            >
              🗑️
            </button>
            <button
              className="tool-circle"
              onClick={undo}
              disabled={strokeHistory.length === 0}
              title="Võta tagasi"
              aria-label="Undo"
            >
              ⤺
            </button>
          </div>

          <div className={`action-buttons ${hideUI ? 'hidden' : ''}`}>
            <button
              className="action-btn secondary"
              onClick={toggleFullscreen}
              aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              {isFullscreen ? "⊡" : "⛶"}
            </button>
            <button
              className="action-btn secondary hide-toggle"
              onClick={() => setHideUI(!hideUI)}
              aria-label={hideUI ? "Show UI" : "Hide UI"}
              title={hideUI ? "Show UI (H)" : "Hide UI (H)"}
            >
              {hideUI ? "👁️" : "◯"}
            </button>
            {/* Image size controls - only show when image exists */}
            {taskImagePreview && (
              <>
                <button
                  className="action-btn secondary"
                  onClick={() => setTaskImageSize(s => ({
                    width: Math.max(100, s.width * 0.85),
                    height: Math.max(80, s.height * 0.85)
                  }))}
                  title="Vähenda pilti"
                >
                  -
                </button>
                <button
                  className="action-btn secondary"
                  onClick={() => setTaskImageSize(s => ({
                    width: Math.min(800, s.width * 1.15),
                    height: Math.min(600, s.height * 1.15)
                  }))}
                  title="Suurenda pilti"
                >
                  +
                </button>
              </>
            )}
            {/* Upload button for free mode (when no assignment) */}
            {!assignmentId && (
              <label className="action-btn secondary" style={{ cursor: 'pointer' }}>
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setTaskImage(file);
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const result = ev.target?.result;
                        if (typeof result === "string") {
                          setTaskImagePreview(result);
                          // Set proper image dimensions and center
                          const img = new Image();
                          img.onload = () => {
                            const maxWidth = 400;
                            const maxHeight = 300;
                            let width = img.naturalWidth;
                            let height = img.naturalHeight;
                            if (width > maxWidth) {
                              height = (height * maxWidth) / width;
                              width = maxWidth;
                            }
                            if (height > maxHeight) {
                              width = (width * maxHeight) / height;
                              height = maxHeight;
                            }
                            setTaskImageSize({ width, height });
                            // Center the image on the canvas
                            const centerX = (window.innerWidth - width) / 2;
                            const centerY = (window.innerHeight - height) / 2;
                            setTaskImagePos({ x: Math.max(0, centerX), y: Math.max(60, centerY) });
                          };
                          img.src = result;
                        }
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                {taskImage ? "Vaheta" : "Laadi"}
              </label>
            )}
            {/* Kontrolli button */}
            <button
              className="action-btn"
              onClick={checkAnswer}
              disabled={isChecking || !taskImage}
              style={{
                background: (!taskImage || isChecking) ? '#9ca3af' : '#22c55e',
                color: 'white',
                fontWeight: '700',
                opacity: (!taskImage || isChecking) ? 0.7 : 1
              }}
            >
              {isChecking ? "..." : "Kontrolli"}
            </button>
          </div>
        </div>

        {/* Feedback Popup */}
        {showPopup && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
            onClick={() => !isChecking && setShowPopup(false)}
          >
            <div
              style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px 32px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                textAlign: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {isChecking ? (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px',
                    animation: 'spin 1s linear infinite'
                  }}>
                    <span role="img" aria-label="Loading">&#9881;</span>
                  </div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>
                    Tehisintellekt kontrollib...
                  </h3>
                  <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>
                    Palun oota
                  </p>
                </>
              ) : (
                <>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px'
                  }}>
                    {feedbackType === 'ok' ? (
                      <span role="img" aria-label="Correct" style={{ color: '#22c55e' }}>&#10004;</span>
                    ) : feedbackType === 'error' ? (
                      <span role="img" aria-label="Error" style={{ color: '#ef4444' }}>&#10060;</span>
                    ) : (
                      <span role="img" aria-label="Info" style={{ color: '#3b82f6' }}>&#128161;</span>
                    )}
                  </div>
                  <h3 style={{
                    margin: '0 0 12px 0',
                    color: feedbackType === 'ok' ? '#22c55e' : feedbackType === 'error' ? '#ef4444' : '#374151'
                  }}>
                    {feedbackType === 'ok' ? 'Tubli!' : feedbackType === 'error' ? 'Viga' : 'Tagasiside'}
                  </h3>
                  <p style={{
                    margin: '0 0 20px 0',
                    color: '#4b5563',
                    fontSize: '15px',
                    lineHeight: '1.5'
                  }}>
                    {feedback}
                  </p>
                  <button
                    onClick={() => setShowPopup(false)}
                    style={{
                      background: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 32px',
                      fontSize: '15px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Sulge
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhiteboardPage;